/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {GraphQLClient} from 'graphql-request';
import {contains} from '@ion/core/src/utils/StringUtil';
import * as logger from './GraphQLLogger';

/**
 * Defines the GraphQL configuration which is required when initializing the client
 * instance. The configuration requires the `serviceURL` and `appKey` parameters, and
 * accepts an optional `authToken`, which, if provided, will configured all request
 * headers to include the authorization header. If the `authToken` is not provided
 * during initialization, it is to be provided post authorization via the `setAuthToken`
 * configuration function.
 *
 * @param serviceURL The API endpoint against which all requests are to be made.
 * @param appKey The application key for which all requests are to be made.
 * @param authToken Optional `JWT` authorization token retrieve via the respective
 *        login API (either CTC or OIDC).
 * @param verboseLogging When enabled, the underlying GraphQL Logger will log all
 *        messages (defaults to `false`).
 */
export interface GraphQLConfig {
  serviceURL: string;
  appKey: string;
  authToken?: string;
  verboseLogging?: boolean;
}

/**
 * @private
 *
 * Provides a reference to the `GraphQLClient` from which all API requests are issued.
 */
let client: GraphQLClient;

/**
 * Defines a reference to the configured GraphQL client instance. This instance
 * is used for issuing all GraphQL requests to the given GraphQL Service API.
 *
 * Note, this function is only to be invoked by the `GraphQL` API and is only
 * exposed in order to provide access to the configured client by the API.
 *
 * ```typescript
 *   // GraphQL.ts
 *   import {getClient} from './GraphQLConfig';
 *   ...
 *
 *   export const getSomething = async(params: GetSomethingParams) => {
 *     const document = createQuery('GetSomething', params);
 *     const results  = await getClient().request<Something>(document)
 *     return results;
 *   };
 *
 */
export const getClient = (): GraphQLClient => client;

/**
 * Provides the main entry point for initializing the `GraphQLClient` instance
 * based on the provided `GraphQLConfig` and optional authorization token retrieved
 * via the respective login API (typically, CTC, or OIDC).
 *
 * Generally, this function should be invoked upon retrieval of the required environment
 * configuration values; i.e, `serviceURL` and `appKey`, and the `setAuthToken` function
 * can subsequently be invoked to provide the authorization JWT post authentication.
 *
 * ```typescript
 *   import {
 *     setAuthToken,
 *     setGraphQLConfig
 *   } from '@ion/services/src/graphql/GraphQLConfig';
 *   ...
 *
 *   // pre-authorization: initialize the graphql client with the provided
 *   // environment configuration (post environment.json retrieval) ...
 *   setGraphQLConfig({
 *     serviceURL : get('serviceURL'),
 *     appKey     : get('appKey')
 *   });
 *
 *   ...
 *
 *   // subsequently invoke setAuthToken post authentication ...
 *   const authToken = await authenticate(username, password);
 *   setAuthToken(authToken);
 * ```
 *
 * Optionally, this function can be invoked immediately after successful login so
 * as to ensure the underlying GraphQL client instance has been initialized prior
 * with the `authToken` provided prior to subsequent requests being made.
 *
 * ```typescript
 *   import {setGraphQLConfig} from '@ion/services/src/graphql/GraphQLConfig';
 *
 *   // post-authorization: initialize the graphql client with the provided
 *   // environment configuration and authorization token retrieved via login ...
 *   const authToken = await authenticate(username, password);
 *   setGraphQLConfig({
 *     serviceURL : get('serviceURL'),
 *     appKey     : get('appKey'),
 *     authToken
 *   });
 *
 * ```
 *
 * @param options The `GraphQLConfig` which specifies the application key and
 *        GraphQL services endpoint, application key, and optional authorization
 *        token (if available) against which all requests are to be made.
 */
export const setGraphQLConfig = (
  {serviceURL, appKey, authToken, verboseLogging = false}: GraphQLConfig, override = false): GraphQLClient => {
  if (!client || override) {
    logger.enableVerboseLogging(verboseLogging);
    const url = resolveServiceURL(serviceURL);
    client = new GraphQLClient(url, {
      credentials : 'include',
      mode        : 'cors',
      headers     : {
        'calamp-services-app' : appKey
      }
    });
    logger.initialize(url, appKey, verboseLogging);
    if (authToken) {
      setAuthToken(authToken);
    }
  } else if (authToken) {
    logger.initialize();
    setAuthToken(authToken);
  }
  return client;
};

/**
 * Provides the single source of entry for applying the authToken to the `authorization`
 * header on the client instance for use by all subsequent GraphQL service requests.
 *
 * Generally, this function is to be invoked post initialization (i.e. `setGraphQLConfig`)
 * after the respective login operation has been successfully invoked and the authorization
 * token has been retrieved.
 *
 * A missing or blank `authToken` is never applied, as the client would otherwise send
 * the literal `undefined` / `null` as the `authorization` header value.
 *
 * @param authToken The authorization token (JWT) provided by the given login service.
 */
export const setAuthToken = (authToken: string, instance: GraphQLClient = client) => {
  if (isValidAuthToken(authToken)) {
    if (instance) {
      const bearer = 'Bearer';
      const token  = authToken.startsWith(bearer) ? authToken : `${bearer} ${authToken}`;
      instance.setHeader('authorization', token);
    }
    logger.setAuthToken(!!instance);
  } else {
    logger.missingAuthToken();
  }
};

/**
 * Determines if the given authorization token is a non-blank string which may be
 * applied to the `authorization` header.
 */
const isValidAuthToken = (authToken: unknown): authToken is string => (
  typeof authToken === 'string' && authToken.trim() !== ''
);

/**
 * Provides a utility for determining if the GraphQL client has been initialized
 * and is ready for use. Initialization verification is performed by determining
 * if the client has been instantiated and that the required headers have been set.
 *
 * ```typescript
 * import {isInitialized} from '@ion/services/src/graphql/';
 *
 * if (!isInitialized()) {
 *   ...
 * }
 * ```
 *
 * @returns `true` if the client has been initialized and is ready for use,
 *          otherwise, `false`.
 */
export const isInitialized = (): boolean => {
  const headers = client?.requestConfig?.headers;
  // eslint-disable-next-line dot-notation
  return !!(headers?.['authorization'] && headers?.['calamp-services-app']);
};

/**
 * Provides a utility which normalizes and resolves the provided service URL to
 * the expected GraphQL endpoint. By default, the `serviceURL` environment.json
 * entry used for defining the Connect Services URL is used for resolving the
 * GraphQL Service URL as both APIs reside on the same domain. The URL is resolved
 * by simply replacing the default `/connect/services` URI with `/api/graphql`.
 *
 * @param serviceURL
 */
const resolveServiceURL = (serviceURL: string): string => {
  const DEFAULT_URL = 'connect/services';
  const GRAPHQL_URI = 'api/graphql';
  if (contains(serviceURL, DEFAULT_URL)) {
    return `${serviceURL.replace(DEFAULT_URL, GRAPHQL_URI)}`;
  } else if (!contains(serviceURL, GRAPHQL_URI)) {
    return `${serviceURL}/${GRAPHQL_URI}`;
  }
  return serviceURL;
};

export default setGraphQLConfig;
